/**
 * The only module in the app that calls `fetch`.
 *
 * Owns: base URL, auth header, JSON codec, correlation id, timeout, cancellation,
 * error normalisation, and the single-flight refresh described below.
 *
 * NOTE ON TENANCY: the backend resolves the tenant from the JWT claim alone. This
 * client never sends a tenant id in a header, query string or body — doing so would
 * be ignored at best and misleading at worst. Switching tenant means getting new
 * tokens (see services/api/auth.ts), not changing a request.
 */

import { API, env } from '@/app/config/env'
import { fromResponse, fromThrown, ApiError } from './errors'

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
  /** Set for the auth endpoints themselves, which must never trigger a refresh. */
  skipAuth?: boolean
  headers?: Record<string, string>
}

type TokenGetter = () => string | null
type RefreshHandler = () => Promise<string | null>
type UnauthorizedHandler = () => void

let getAccessToken: TokenGetter = () => null
let refreshTokens: RefreshHandler = async () => null
let onUnauthorized: UnauthorizedHandler = () => {}

/** Wired once by AuthProvider. Keeps the client free of React imports. */
export function configureApiClient(handlers: {
  getAccessToken: TokenGetter
  refreshTokens: RefreshHandler
  onUnauthorized: UnauthorizedHandler
}): void {
  getAccessToken = handlers.getAccessToken
  refreshTokens = handlers.refreshTokens
  onUnauthorized = handlers.onUnauthorized
}

/**
 * Single-flight refresh.
 *
 * Refresh tokens ROTATE: the backend deletes the old one as it issues the new pair.
 * If ten parallel table queries each fired their own refresh on a 401, the first
 * would succeed and the other nine would present an already-revoked token and fail,
 * logging the user out mid-session. So every concurrent 401 awaits one promise.
 */
let refreshInFlight: Promise<string | null> | null = null

function refreshOnce(): Promise<string | null> {
  refreshInFlight ??= refreshTokens().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${env.apiBaseUrl}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

function newCorrelationId(): string {
  return crypto.randomUUID()
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    const text = await response.text()
    return text.length ? text : null
  }
  try {
    return await response.json()
  } catch {
    return null
  }
}

async function send(path: string, options: RequestOptions, correlationId: string): Promise<Response> {
  const { method = 'GET', body, query, signal, skipAuth, headers = {} } = options

  const timeout = AbortSignal.timeout(env.requestTimeoutMs)
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-ID': correlationId,
    ...headers,
  }
  if (body !== undefined) requestHeaders['Content-Type'] = 'application/json'

  if (!skipAuth) {
    const token = getAccessToken()
    if (token) requestHeaders.Authorization = `Bearer ${token}`
  }

  return fetch(buildUrl(path, query), {
    method,
    headers: requestHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: combined,
  })
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const correlationId = newCorrelationId()
  let response: Response

  try {
    response = await send(path, options, correlationId)
  } catch (cause) {
    // A caller-initiated abort is a cancellation; anything else that aborted is a timeout.
    throw fromThrown(cause, !options.signal?.aborted)
  }

  // Expired access token: refresh once, then replay. Never for the auth endpoints
  // themselves, or a failing refresh would recurse.
  if (response.status === 401 && !options.skipAuth) {
    const token = await refreshOnce()
    if (token) {
      try {
        response = await send(path, options, correlationId)
      } catch (cause) {
        throw fromThrown(cause, !options.signal?.aborted)
      }
    } else {
      onUnauthorized()
      throw fromResponse(401, await parseBody(response), response.headers, correlationId)
    }
  }

  const body = await parseBody(response)

  if (!response.ok) {
    if (response.status === 401) onUnauthorized()
    throw fromResponse(response.status, body, response.headers, correlationId)
  }

  return body as T
}

/** A file that came back from the API, ready to be handed to the browser. */
export interface DownloadResult {
  blob: Blob
  filename: string
}

/**
 * RFC 6266 `Content-Disposition` -> filename.
 *
 * The server chooses the name because it knows whether the document is final or
 * interim; the browser would otherwise name the file after the URL path ("pdf").
 */
function filenameFrom(header: string | null, fallback: string): string {
  if (!header) return fallback
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || fallback
}

/**
 * Binary sibling of `request`. Same auth, refresh and error handling — a download
 * that silently skipped the single-flight refresh would log the user out.
 */
async function requestBlob(
  path: string,
  accept: string,
  fallbackFilename: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<DownloadResult> {
  const correlationId = newCorrelationId()
  const withAccept: RequestOptions = {
    ...options,
    method: 'GET',
    headers: { ...options.headers, Accept: accept },
  }

  let response: Response
  try {
    response = await send(path, withAccept, correlationId)
  } catch (cause) {
    throw fromThrown(cause, !options.signal?.aborted)
  }

  if (response.status === 401) {
    const token = await refreshOnce()
    if (token) {
      try {
        response = await send(path, withAccept, correlationId)
      } catch (cause) {
        throw fromThrown(cause, !options.signal?.aborted)
      }
    } else {
      onUnauthorized()
      throw fromResponse(401, await parseBody(response), response.headers, correlationId)
    }
  }

  if (!response.ok) {
    // The failure body is our usual JSON envelope even though we asked for a PDF.
    if (response.status === 401) onUnauthorized()
    throw fromResponse(response.status, await parseBody(response), response.headers, correlationId)
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('content-disposition'), fallbackFilename),
  }
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  /** GET a binary document. Returns the bytes and the server's chosen filename. */
  download: (
    path: string,
    accept: string,
    fallbackFilename: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => requestBlob(path, accept, fallbackFilename, options),
}

/** Path builders — no route string is ever written at a call site. */
export const paths = {
  v1: (segment: string) => `${API.v1}${segment}`,
  admin: (segment: string) => `${API.admin}${segment}`,
}

export { ApiError }
